import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Megaphone, Building2, Film, Users, CheckCircle2, Clock, AlertTriangle, Send, ArrowLeft, Sparkles,
} from "lucide-react";

/**
 * Public campaign lead form.
 *
 * Flow:
 *  1. User fills form -> POST /api/public/leads with a client-generated
 *     idempotency_key (safe to retry if the browser double-submits).
 *  2. Backend persists locally + fans out to work.mirrorloops.com.
 *  3. We only tell the user "sent" when the backend reports a
 *     delivered OR pending_delivery status (both mean we have the lead).
 *
 * Deliberately NOT auth-gated: this is the top-of-funnel for all three
 * audiences — advertisers, venue owners, and media owners. Internal
 * account creation lives at /signup.
 */

const LEAD_TYPES = [
  { v: "advertiser", label: "Launch a campaign", blurb: "Buy programmable screen time across the Mirrorloops network.", icon: Megaphone },
  { v: "venue", label: "Connect my venue", blurb: "I run a cafe, salon, gym or clinic and want screens that work.", icon: Building2 },
  { v: "media_owner", label: "Join as a media owner", blurb: "I own screens and want programmatic demand on them.", icon: Film },
  { v: "operator", label: "Operator / franchise", blurb: "I want to run a Mirrorloops hub or operate the network.", icon: Users },
];

const REGIONS = [
  "India — North", "India — South", "India — East", "India — West", "India — National",
  "UAE", "United Kingdom", "United States", "Australia", "Singapore", "Other",
];

function makeIdempotencyKey() {
  // Simple RFC-4122 v4-ish — good enough for request-dedup and cheaper
  // than pulling a crypto library. The backend will accept whatever
  // string we send.
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "lead-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

export default function Register() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", region: "", message: "", type: "advertiser",
  });
  // Hold a single idempotency key for the lifetime of the form. If the
  // network layer retries after 5xx, both attempts carry the same key
  // and the backend returns the same lead_id.
  const idemKey = useMemo(() => makeIdempotencyKey(), []);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { lead_id, delivery:{status,...}}

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        type: form.type,
        region: form.region || null,
        message: form.message.trim() || null,
        source: "mirrorloops.com/register",
        idempotency_key: idemKey,
      };
      const r = await api.post("/public/leads", payload);
      setResult(r.data);
      const s = r.data?.delivery?.status;
      if (s === "delivered") {
        toast.success("Lead delivered. Our team will reach out shortly.");
      } else if (s === "pending_delivery") {
        toast.success("We've received your details — delivering to our team now.");
      } else if (s === "rejected") {
        toast.error("Something was off with your submission. We'll follow up by email.");
      } else {
        toast.success("Received. Our team will respond soon.");
      }
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Couldn't submit — please try again.";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw.length) {
        msg = raw.map((r) => (r?.msg ? `${(r.loc || []).slice(-1)[0] || "field"}: ${r.msg}` : String(r))).join(" · ");
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const s = result.delivery?.status;
    const Icon = s === "delivered" ? CheckCircle2 : s === "rejected" ? AlertTriangle : Clock;
    const tone = s === "delivered"
      ? "border-[#CCFF00]/40 bg-[#CCFF00]/[0.05] text-[#CCFF00]"
      : s === "rejected"
      ? "border-red-400/40 bg-red-500/[0.05] text-red-300"
      : "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3]";
    return (
      <PublicShell>
        <div data-testid="register-result" className="max-w-xl mx-auto px-6 pt-40 pb-24">
          <div className={`border rounded-lg p-6 ${tone}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={20} />
              <div className="text-lg font-semibold text-white">
                {s === "delivered" && "Delivered to our team"}
                {s === "pending_delivery" && "Received — delivering now"}
                {s === "rejected" && "Submission needs attention"}
                {!s && "Received"}
              </div>
            </div>
            <div className="text-sm text-white/70 leading-relaxed">
              {s === "delivered" && "A member of our commercial team will reach out within 1 business day. We've CC'd your details to the right queue based on what you selected."}
              {s === "pending_delivery" && "We've stored your details and we're actively delivering them to our commercial team. You'll hear back within 1 business day."}
              {s === "rejected" && "There was a validation issue with your submission. We've logged it and someone will reach out by email to continue."}
            </div>
            <div className="mt-5 pt-5 border-t border-white/10 flex flex-col sm:flex-row gap-3 text-xs text-white/50 font-mono">
              <span data-testid="register-result-lead-id">Lead ID · {result.lead_id?.slice(0, 8)}</span>
              <span>Idempotency · {idemKey.slice(0, 8)}</span>
              <span className="uppercase tracking-[0.14em]">Status · {s || "received"}</span>
            </div>
          </div>
          <div className="mt-8 flex gap-3">
            <Link
              to="/"
              className="rounded-full border border-white/15 text-white hover:bg-white/5 px-5 py-2 text-sm flex items-center gap-1.5"
            >
              <ArrowLeft size={13} /> Back to home
            </Link>
            <Link
              to="/store"
              className="rounded-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold px-5 py-2 text-sm"
            >
              Explore the Store →
            </Link>
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <SEO
        title="Register — Launch a Campaign"
        description="Tell Mirrorloops what you want on screens. Advertisers, agencies, venues, and media owners submit here — direct handoff to our commercial team, no retargeting, no third-party sharing."
        path="/register"
      />
      <div data-testid="register-page" className="max-w-6xl mx-auto px-6 pt-32 pb-24 grid lg:grid-cols-5 gap-10">
        {/* Left column — context */}
        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-2 flex items-center gap-2">
            <Sparkles size={12} /> Public inbox · lead handoff
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white leading-tight">
            Tell us what you want to put on screens.
          </h1>
          <p className="mt-4 text-white/60 leading-relaxed">
            Every submission goes directly to the Mirrorloops commercial team. We don't tracking-pixel you,
            we don't retarget you, and we don't share your details with third parties.
          </p>
          <div className="mt-6 p-4 border border-white/10 rounded bg-white/[0.02] text-xs text-white/50 leading-relaxed">
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mb-1 font-mono">
              What happens next
            </div>
            <ol className="list-decimal pl-4 space-y-1">
              <li>We persist your submission on this side.</li>
              <li>We hand it to <span className="font-mono text-[#D4B878]">work.mirrorloops.com</span> with an idempotency key — repeat submits never duplicate.</li>
              <li>Our commercial team picks it up from the Work inbox.</li>
            </ol>
          </div>
        </div>

        {/* Right column — form */}
        <form onSubmit={submit} className="lg:col-span-3 space-y-5 border border-white/10 rounded-lg p-6 bg-white/[0.02]">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 block">
              I want to…
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {LEAD_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.v}
                  onClick={() => setForm({ ...form, type: t.v })}
                  data-testid={`register-type-${t.v}`}
                  className={`text-left rounded border px-3 py-3 transition ${
                    form.type === t.v
                      ? "border-[#D4B878]/60 bg-[#D4B878]/[0.08] text-white"
                      : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <t.icon size={13} />
                    <div className="text-xs font-semibold">{t.label}</div>
                  </div>
                  <div className="text-[10px] text-white/50 mt-1 leading-snug">{t.blurb}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Name *</Label>
              <Input
                data-testid="register-name"
                required
                value={form.name}
                onChange={set("name")}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Email *</Label>
              <Input
                data-testid="register-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="jane@brand.com"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Company</Label>
              <Input
                data-testid="register-company"
                value={form.company}
                onChange={set("company")}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Brand or agency name"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Region</Label>
              <Select value={form.region} onValueChange={set("region")}>
                <SelectTrigger
                  data-testid="register-region"
                  className="bg-white/5 border-white/10 mt-1"
                >
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Tell us more (optional)</Label>
            <textarea
              data-testid="register-message"
              rows={3}
              value={form.message}
              onChange={set("message")}
              placeholder="Timeline, budget range, cities, screens you already have, anything else"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none"
            />
          </div>

          <div className="text-[10px] text-white/40 font-mono">
            Idempotency key · <span className="text-white/60">{idemKey.slice(0, 12)}…</span>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="register-submit"
            className="w-full rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold"
          >
            <Send size={13} className="mr-1.5" />
            {busy ? "Submitting…" : "Submit lead"}
          </Button>

          <div className="text-[11px] text-white/40 text-center">
            Already have a Mirrorloops account?{" "}
            <Link to="/login" className="text-[#E8D6A3] font-semibold hover:underline">
              Sign in
            </Link>{" "}
            · Need an internal signup?{" "}
            <Link to="/signup" className="text-[#E8D6A3] font-semibold hover:underline">
              Create account
            </Link>
          </div>
        </form>
      </div>
    </PublicShell>
  );
}
